/**
 * Service context provider for dependency injection in tests.
 * Provides isolated service instances per test render, enabling better test isolation.
 * In production, services fall back to singletons when context is not provided.
 *
 * Also supports scheduler/clock injection for deterministic timer testing.
 */

import type { ReactNode } from 'react'
import React, { createContext, useContext } from 'react'
import type { ApiService } from '../services/api'
import type { AuthService } from '../services/auth.service'
import type { ReviewService } from '../services/review.service'
import type { PersonalVocabService } from '../services/personalVocab.service'
import type { Scheduler } from '../lib/scheduler'
import type { Clock } from '../lib/clock'

export interface ServiceContextType {
    /** API service instance (optional - hooks fall back to singleton if not provided) */
    apiService?: ApiService
    /** Auth service instance (optional - hooks fall back to singleton if not provided) */
    authService?: AuthService
    /** Review service instance (optional - hooks fall back to singleton if not provided) */
    reviewService?: ReviewService
    /** Personal vocab service instance (optional - hooks fall back to singleton if not provided) */
    personalVocabService?: PersonalVocabService
    /** Scheduler instance for timer operations (optional - hooks fall back to browserScheduler if not provided) */
    scheduler?: Scheduler
    /** Clock instance for time operations (optional - hooks fall back to systemClock if not provided) */
    clock?: Clock
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined)

export { ServiceContext }

interface ServiceProviderProps {
    children: ReactNode
    /** Service instances to provide (optional - hooks fall back to singletons if not provided) */
    services?: ServiceContextType
}

/**
 * Service context provider.
 * Provides service instances to child components via context.
 * Hooks can read from this context and fall back to singletons if not provided.
 */
export const ServiceProvider: React.FC<ServiceProviderProps> = ({ children, services }) => {
    return <ServiceContext.Provider value={services}>{children}</ServiceContext.Provider>
}

/**
 * Hook to access service context.
 * Returns service instances from context, or undefined if not provided.
 * Hooks should use this and fall back to singletons.
 */
// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useServiceContext = (): ServiceContextType | undefined => {
    return useContext(ServiceContext)
}
